import apiClient from "@/services/api/client";
import urlBuilder from "@/services/api/urlBuilder";
import { DEFAULT_ORDERS_PAGE_SIZE } from "@/shared/utils/constants";
import { buildOrdersGlobalFilter, type OrdersGlobalFilterInput } from "../buildOrdersGlobalFilter";

// V4 orders read API (Task 6). OrderV4 is a separate collection with its own
// endpoints (order-getOrdersV4 / order-getOrderV4) and id space — kept in a
// dedicated module so the large v3 ordersApi.ts is untouched.

export interface ParcelDimensions {
  length_cm?: number;
  width_cm?: number;
  height_cm?: number;
  weight_kg?: number;
}

export interface OrderV4Parcel {
  parcel_index?: number;
  description?: string;
  declared?: ParcelDimensions | null;
  /** Courier-reported (TFS) dims/weight; absent until the courier measures the parcel. */
  measured?: ParcelDimensions | null;
  /** Hub-measured dims/weight when available, else mirrors declared. */
  effective?: ParcelDimensions | null;
  volumetric_weight_kg?: number | null;
  billable_weight_kg?: number | null;
  items_packed?: Array<{ name?: string; sku?: string; quantity?: number }>;
}

export type ShipmentArea = "local" | "main" | "regional" | "remote";

export const SHIPMENT_AREA_LABELS: Record<ShipmentArea, string> = {
  local: "Local",
  main: "Main",
  regional: "Regional",
  remote: "Remote",
};

/** One side of the declared-vs-measured pricing view (shipment.pricing.declared / .measured). */
export interface ShipmentPricingSnapshot {
  billable_kg?: number;
  parcel_bands?: string[];
  per_parcel?: Array<{
    index?: number;
    billable_weight_kg?: number;
    band?: string;
    cost?: number;
  }>;
  surcharges?: Array<{ code?: string; amount?: number }>;
  cost?: number;
  priced_at?: string;
  measured_by?: string;
  measured_at?: string;
}

export interface ShipmentPricing {
  basis?: "per_parcel" | "per_shipment";
  area?: ShipmentArea;
  area_code?: string;
  declared?: ShipmentPricingSnapshot | null;
  measured?: ShipmentPricingSnapshot | null;
  /** Last settlement, signed: negative is a credit to the merchant. */
  adjustment?: number;
  status?: "charged" | "credited" | "no_change" | "ops_review";
  status_reason?: string;
  adjusted_at?: string;
  ledger_entries?: string[];
}

export interface OrderV4Shipment {
  _id?: string;
  waybill?: string;
  status?: string;
  milestone?: string;
  parcels?: OrderV4Parcel[];
  delivery?: {
    contact?: { name?: string; phone?: string };
    address?: { formatted?: string };
    proof?: { pdf_link?: string | null } | null;
  } | null;
  // courier arrives as the populated object (shipment.courier), not a string.
  // `service_type` is the legacy string enum (never written); the service type
  // resolved at creation/booking is snapshotted onto the *_code / *_name fields,
  // so nothing here needs a populate.
  courier?: {
    service_type?: string;
    service_type_code?: string;
    service_type_name?: string;
    /** "HH:mm" snapshot of the service type's booking cut-off. */
    booking_cutoff_time?: string;
    provider?: string;
    cost?: number;
    area?: ShipmentArea;
    area_source?: "tfs" | "fallback" | "manual";
    planned?: {
      collection_at?: string | null;
      at_hub_at?: string | null;
      delivery_at?: string | null;
    } | null;
  } | null;
  // Third-party courier (e.g. TFS). True only when this shipment's delivery
  // leg is actually fulfilled by a third party — the underlying backend field
  // can be present without this being true (a merchant with the provider
  // enabled still onboards its in-area orders for pricing/audit purposes even
  // though Breeze's own driver network delivers them) — only the resolved
  // boolean is needed here, never the provider name itself.
  thirdPartyCourier?: {
    fulfilled_by_provider?: boolean;
  } | null;
  shipment_type?: string;
  // getOrderV4's shipment select is exclusion-based (everything except
  // dispatch_lease/dispatch_reversals), so this and problem_solve are both
  // present on Order/Shipment detail — NOT on the Orders list's shipment
  // select, which omits both (AddToProblemSolveButton's pre-check props are
  // left unset wherever a surface only has the narrower list shape).
  rto_locked?: boolean;
  problem_solve?: {
    state?: "awaiting_arrival" | "at_station" | "awaiting_feedback" | "reply_received" | "resolved";
  } | null;
  // Absent on shipments booked before weight-banded pricing; absent high_risk means not flagged.
  high_risk?: boolean;
  billing?: { status?: string; charged_amount?: number } | null;
  pricing?: ShipmentPricing | null;
  // breaze_driver / breaze_manifest may arrive populated (object) or as a bare id.
  breaze_driver?: { _id?: string; name?: string } | string | null;
  breaze_manifest?: { _id?: string; manifest_key?: string } | string | null;
  // Tookan-side assignment write-back; `driver` is null when the fleet is unmapped.
  tookan_driver?: {
    driver?: { _id?: string; name?: string } | string | null;
    fleet_id?: number;
    fleet_name?: string;
    assigned_at?: string;
    job_status?: number;
    leg?: string;
  } | null;
  [key: string]: unknown;
}

export interface OrderV4 {
  _id: string;
  breaze_order_key?: string;
  status?: string;
  // tri-state: true | false | null (null = eligibility indeterminate, e.g. a
  // region-API outage at create time). Render null as "Unknown", never "No".
  breaze_eligible?: boolean | null;
  platform?: string;
  createdAt?: string;
  updatedAt?: string;
  order_id?: string;
  merchant_reference?: string;
  line_items?: Array<{
    name?: string;
    quantity?: number;
    selling_price?: number;
    cost_price?: number;
  }>;
  financials?: {
    sub_total?: number;
    shipping_fee?: number;
    total?: number;
    currency?: string;
  };
  note?: string;
  customer?: {
    name?: string;
    email?: string;
    phone?: string;
    breaze_ref?: string | { _id?: string };
  } | null;
  address?: Record<string, unknown> | null;
  breaze_merchant?: { _id?: string; name?: string } | null;
  breaze_store?: { _id?: string; store_name?: string } | null;
  breaze_location?: { _id?: string; name?: string; address?: string } | null;
  shipments?: OrderV4Shipment[];
  platform_metadata?: unknown;
  [key: string]: unknown;
}

export interface OrdersV4Response {
  docs: OrderV4[];
  page: number;
  totalPages: number;
  totalDocs: number;
  limit?: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
}

export interface GetOrdersV4Params {
  page?: number;
  limit?: number;
  globalFilterEnabled?: boolean;
  globalFilter?: {
    status?: string | string[];
    /** Shipment-level status/milestone, separate from the order-level `status`. */
    shipmentStatus?: string | string[];
    platform?: string | string[];
    /** courier.service_type_code, matches if any shipment on the order has it. */
    service_type_code?: string | string[];
    orderId?: string;
    orderIds?: string[];
    waybill?: string;
    waybills?: string[];
    breaze_manifests?: string[];
    customer?: string;
    dateFrom?: string;
    dateTo?: string;
    status_not_in?: string;
    past_sla?: boolean;
    stale?: boolean;
    updatedAtFrom?: string;
    updatedAtTo?: string;
  };
  merchantId?: string;
  storeId?: string;
  locationId?: string;
  driverId?: string;
  /** Note: the backend gives `locationId` priority over `hubId` when both are set. */
  hubId?: string;
  csv?: boolean;
  csvDetailed?: boolean;
}

export type OrdersV4ListFilters = OrdersGlobalFilterInput & {
  merchantId?: string;
  storeId?: string;
  locationId?: string;
  driverId?: string;
  hubId?: string;
  drillGlobalExtras?: Record<string, string | boolean>;
};

export function buildOrdersV4ListParams(options: {
  page: number;
  limit: number;
  filters: OrdersV4ListFilters;
  csv?: boolean;
  csvDetailed?: boolean;
}): GetOrdersV4Params {
  const { filters } = options;
  const globalFilter: NonNullable<GetOrdersV4Params["globalFilter"]> = {
    ...buildOrdersGlobalFilter(filters),
  };
  if (filters.drillGlobalExtras) {
    for (const [key, value] of Object.entries(filters.drillGlobalExtras)) {
      if (value !== undefined && value !== "") {
        (globalFilter as Record<string, unknown>)[key] = value;
      }
    }
  }
  return {
    page: options.page,
    limit: options.limit,
    globalFilter,
    merchantId: filters.merchantId || undefined,
    storeId: filters.storeId || undefined,
    locationId: filters.locationId || undefined,
    driverId: filters.driverId || undefined,
    hubId: filters.hubId || undefined,
    csv: options.csv,
    csvDetailed: options.csvDetailed,
  };
}

export function buildOrdersV4CsvExportParams(
  listParams: GetOrdersV4Params,
  options?: { csvDetailed?: boolean }
): GetOrdersV4Params {
  return {
    ...listParams,
    page: 1,
    csv: true,
    csvDetailed: options?.csvDetailed ?? listParams.csvDetailed,
  };
}

function buildOrdersV4RequestBody(params: GetOrdersV4Params): Record<string, unknown> {
  const requestBody: Record<string, unknown> = {
    globalFilterEnabled: params.globalFilterEnabled !== false,
    globalFilter: params.globalFilter ?? {},
  };
  if (params.storeId) requestBody.storeId = params.storeId;
  if (params.locationId) requestBody.locationId = params.locationId;
  if (params.driverId) requestBody.driverId = params.driverId;
  if (params.merchantId) requestBody.merchantId = params.merchantId;
  if (params.hubId) requestBody.hubId = params.hubId;
  if (params.csv) requestBody.csv = true;
  if (params.csvDetailed) requestBody.csvDetailed = true;
  return requestBody;
}

/** Paginated V4 orders list. Page/limit are query params; filters go in the POST body (mirrors getOrdersV2). */
export async function getOrdersV4(params: GetOrdersV4Params = {}): Promise<OrdersV4Response> {
  const page = params.page ?? 1;
  const limit = params.limit ?? DEFAULT_ORDERS_PAGE_SIZE;
  const url = `${urlBuilder.buildUrl("order-getOrdersV4")}?limit=${limit}&page=${page}`;
  // This endpoint can run long on large/filtered pages — give it more room than
  // the client's default 30s timeout.
  const response = await apiClient.post(url, buildOrdersV4RequestBody(params), {
    timeout: 120000,
  });
  const data = (response.data?.data ?? response.data ?? {}) as Partial<OrdersV4Response>;
  return {
    docs: data.docs ?? [],
    page: data.page ?? page,
    totalPages: data.totalPages ?? 1,
    totalDocs: data.totalDocs ?? data.docs?.length ?? 0,
    limit: data.limit ?? limit,
    hasNextPage: data.hasNextPage,
    hasPrevPage: data.hasPrevPage,
  };
}

/** CSV export for V4 orders — same filter envelope as the list. */
export async function exportOrdersV4Csv(params: GetOrdersV4Params): Promise<Blob> {
  const page = 1;
  const limit = params.limit || DEFAULT_ORDERS_PAGE_SIZE;
  const url = `${urlBuilder.buildUrl("order-getOrdersV4")}?limit=${limit}&page=${page}`;
  const response = await apiClient.post(
    url,
    buildOrdersV4RequestBody({ ...params, csv: true, csvDetailed: !!params.csvDetailed }),
    { responseType: "blob", timeout: 120000 }
  );
  return response.data as Blob;
}

/** Single V4 order by id. getOrderV4 returns `{ order }`. */
export async function getOrderV4(orderId: string): Promise<OrderV4 | null> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-getOrderV4"), { orderId });
  const raw = response.data?.data ?? response.data;
  if (raw && typeof raw === "object" && "order" in raw && raw.order) {
    return raw.order as OrderV4;
  }
  return (raw as OrderV4) ?? null;
}

export interface ShipmentActionV4Payload {
  /** The source shipment's waybill — both Ship Again and Return only need this. */
  waybill: string;
}

export interface ShipmentActionV4Result {
  waybill: string;
  shipment_id: string;
  source_waybill: string;
  breaze_eligible: boolean | null;
  milestone: string;
}

/** Books a fresh delivery on the same order, reusing the source shipment's details. */
export async function shipAgainV4(
  payload: ShipmentActionV4Payload
): Promise<ShipmentActionV4Result> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-shipAgainV4"), payload);
  const data = response.data?.data ?? response.data;
  return data as ShipmentActionV4Result;
}

/** Creates a reverse-direction (customer → merchant) shipment on the same order. */
export async function returnShipmentV4(
  payload: ShipmentActionV4Payload
): Promise<ShipmentActionV4Result> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-returnShipmentV4"), payload);
  const data = response.data?.data ?? response.data;
  return data as ShipmentActionV4Result;
}

export interface RtoShipmentV4Result extends ShipmentActionV4Result {
  status: string;
}

/**
 * Admin-only. Creates an internal hub → merchant Return To Origin shipment from a
 * source shipment currently sitting `At Hub` or `Failed` — never involves the
 * customer. `breaze_eligible` can be true/false/null even on a `success: true`
 * response; only `true` (or `milestone === 'InManifest'`) means it's fully booked.
 */
export async function rtoShipmentV4(
  payload: ShipmentActionV4Payload
): Promise<RtoShipmentV4Result> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-rtoShipmentV4"), payload);
  const data = response.data?.data ?? response.data;
  return data as RtoShipmentV4Result;
}

export interface IncreaseShipmentParcelsV4Payload {
  waybill: string;
  /** Target total parcel count, not an increment — must be > current count and <= 4. */
  parcel_count: number;
}

export interface IncreaseShipmentParcelsV4Result {
  waybill: string;
  parcel_count: number;
  /** Same printer-queue/{jobId} job the existing auto-print flow already polls. */
  jobId: string;
}

/**
 * Any authenticated portal user, same as updateOrderV4 (not admin-only). Reprints EVERY
 * parcel on the shipment, not just the new ones — each label encodes its position ("N of
 * total"), so a count change makes all previously-printed labels for this shipment stale.
 * New parcels clone the shipment's last parcel's dimensions (or the merchant's default
 * package type) — there's no way to specify custom dimensions through this endpoint.
 */
export async function increaseShipmentParcelsV4(
  payload: IncreaseShipmentParcelsV4Payload
): Promise<IncreaseShipmentParcelsV4Result> {
  const response = await apiClient.post(
    urlBuilder.buildUrl("order-increaseShipmentParcelsV4"),
    payload
  );
  const data = response.data?.data ?? response.data;
  return data as IncreaseShipmentParcelsV4Result;
}

export type ShipmentLabelEventAction = "print" | "download";

/**
 * Fire-and-forget usage logging for a shipment label print/download — never awaited by
 * callers, and failures are swallowed here so a logging hiccup can never surface to the
 * user or interfere with the actual print/download it's recording. Not applicable to
 * packing slips — only shipping-label prints/downloads get logged.
 */
export function logShipmentLabelEvent(waybill: string, action: ShipmentLabelEventAction): void {
  if (!waybill) return;
  void apiClient
    .post(urlBuilder.buildUrl("order-logShipmentLabelEventV4"), { waybill, action })
    .catch(() => {});
}

/** Dedupes waybills (a multi-parcel shipment can appear more than once in one batch) before
 *  firing one independent, non-awaited log call per waybill. */
export function logShipmentLabelEvents(
  waybills: Array<string | undefined>,
  action: ShipmentLabelEventAction
): void {
  const unique = new Set(waybills.filter((w): w is string => !!w));
  for (const waybill of unique) {
    logShipmentLabelEvent(waybill, action);
  }
}

export interface ShipmentLabelEventCounts {
  waybill: string;
  print_count: number;
  download_count: number;
}

/**
 * Checked before a single-shipment PDF/Zebra bulk action, to offer a "you've already done
 * this N times — redownload/reprint, or increase parcels first?" confirmation. This is a
 * nice-to-have guard, never a gate on the real action: any failure here (network error,
 * 404, etc.) resolves to `null` rather than throwing, so callers can fail open and proceed
 * with the print/download as if there were no prior events.
 */
export async function getShipmentLabelEventCountsV4(
  waybill: string
): Promise<ShipmentLabelEventCounts | null> {
  try {
    const response = await apiClient.post(
      urlBuilder.buildUrl("order-getShipmentLabelEventCountsV4"),
      { waybill }
    );
    const data = response.data?.data ?? response.data;
    return data as ShipmentLabelEventCounts;
  } catch {
    return null;
  }
}

// --- V4 custom-order creation (Task 7) — portal sibling of v3 createCustomOrder. ---

export interface CreateCustomOrderV4Address {
  formatted?: string;
  address1?: string;
  city?: string;
  province?: string;
  zip?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
}

export interface CreateCustomOrderV4Parcel {
  length_cm: number;
  width_cm: number;
  height_cm: number;
  weight_kg: number;
}

export interface CreateCustomOrderV4Payload {
  storeId: string;
  pickupLocationId: string;
  orderId?: string;
  customer: { ref?: string; name: string; email?: string; phone?: string };
  delivery: { address: CreateCustomOrderV4Address; note?: string };
  /** 1 to 4 parcels — each becomes its own delivery shipment (own waybill) server-side. */
  parcels: CreateCustomOrderV4Parcel[];
  /** Set to 1 to immediately book every Breaze-Eligible shipment with a driver. */
  request_shipment?: 0 | 1;
}

/** The created OrderV4 (with `_id` for navigation) plus the backend status message. */
export interface CreateCustomOrderV4Result {
  order: OrderV4;
  /** Backend status message — surfaces partial `request_shipment` booking failures. */
  message: string;
}

export async function createCustomOrderV4(
  payload: CreateCustomOrderV4Payload
): Promise<CreateCustomOrderV4Result> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-createCustomOrderV4"), payload);
  const body = response.data ?? {};
  const raw = body?.data ?? body;
  const order = (
    raw && typeof raw === "object" && "order" in raw && raw.order ? raw.order : raw
  ) as OrderV4;
  const message = typeof body?.message === "string" ? body.message : "";
  return { order, message };
}

// --- V4 order edit — portal sibling of updateOrder (JWT), backed by order-updateOrderV4. ---

export interface UpdateOrderV4Parcel {
  description?: string;
  declared: ParcelDimensions;
  items_packed?: Array<{ name?: string; sku?: string; quantity?: number }>;
}

/** Parcels are keyed to a shipment by waybill (preferred) or _id. */
export interface UpdateOrderV4ShipmentInput {
  waybill?: string;
  _id?: string;
  parcels: UpdateOrderV4Parcel[];
}

export interface UpdateOrderV4Payload {
  orderId: string;
  order_id?: string;
  customer?: { name?: string; email?: string; phone?: string };
  delivery?: { address?: CreateCustomOrderV4Address; note?: string };
  shipments?: UpdateOrderV4ShipmentInput[];
}

export async function updateCustomOrderV4(payload: UpdateOrderV4Payload): Promise<OrderV4> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-updateOrderV4"), payload);
  const body = response.data ?? {};
  const raw = body?.data ?? body;
  return (
    raw && typeof raw === "object" && "order" in raw && raw.order ? raw.order : raw
  ) as OrderV4;
}

// --- Collection Failed reinstate — admin-only manifest-requestCollectionReattempt. ---

export interface RequestCollectionReattemptPayload {
  shipmentId: string;
}

export interface RequestCollectionReattemptResult {
  shipmentId?: string;
  waybill?: string;
  milestone?: string;
  status?: string;
}

export async function requestCollectionReattempt(
  payload: RequestCollectionReattemptPayload
): Promise<RequestCollectionReattemptResult> {
  const response = await apiClient.post(
    urlBuilder.buildUrl("manifest-requestCollectionReattempt"),
    { shipmentId: payload.shipmentId }
  );
  return (response.data?.data ?? response.data ?? {}) as RequestCollectionReattemptResult;
}

// --- Comments — internal notes on an order or a shipment, rolled up by order.
// Admins compose and edit; every role can read (the backend scopes and redacts). ---

export type CommentVisibility = "private" | "public";

// problem-solve-scope.md §5 — verified against the real, evolved
// addShipmentCommentV4/listShipmentCommentsV4/editCommentV4 on 2026-10-07.
// `author_party`/`tags` are new; `visibility` is kept and still written
// (derived from tags) for back-compat with older callers/readers. Crucially,
// `author` in a listShipmentComments row is ALREADY the fully-resolved,
// per-viewer display string (e.g. "Breaze Support" for a non-Breeze viewer
// looking at a Breeze author) — services/commentVisibility.js's
// canView/renderAuthor run entirely server-side. The frontend does ZERO
// hiding or author-rendering logic of its own; it just displays `author`
// and the already-filtered `comments` array verbatim. Likewise `author_id`/
// `edited_by`/`editor` are already null for any non-Admin viewer — never a
// raw user id a Merchant/customer shouldn't see.
export type CommentParty = "breaze" | "merchant" | "customer";

export interface ShipmentComment {
  id: string;
  scope: "order" | "shipment";
  shipment_id: string | null;
  waybill: string | null;
  order_id: string | null;
  comment: string;
  visibility: string;
  author_party: CommentParty;
  /** Who this comment is additionally visible to, beyond Breeze (who always
   *  sees everything) and the comment's own author_party. */
  tags: CommentParty[];
  /** Admin-only @mention of specific other admins (Problem Solve "Tagged"
   *  stat card) — always both present and the same length, resolved at
   *  write time server-side. Empty when nobody specific was mentioned. */
  mentioned_admin_ids: string[];
  mentioned_admin_names: string[];
  author: string | null;
  author_id: string | null;
  created_at: string;
  edited_at: string | null;
  edited_by: string | null;
  editor: string | null;
}

export interface AddShipmentCommentPayload {
  shipmentId?: string;
  waybill?: string;
  orderId?: string;
  comment: string;
  /** Explicit tags win over `visibility`. Omit both to get the backend's own
   *  default (no tags for a Breeze author — an internal note; `['breaze']`
   *  for a Merchant author). */
  tags?: CommentParty[];
  visibility?: CommentVisibility;
  /** Admin-only @mention of specific other admins — silently ignored by the
   *  backend for a Merchant/StoreManager author. */
  mentioned_admin_ids?: string[];
}

/** Only the supplied fields are written; the backend rejects a payload with neither.
 *  Admin only — this one still speaks `visibility`, not `tags`. */
export interface EditCommentPayload {
  commentId: string;
  comment?: string;
  visibility?: CommentVisibility;
}

/** Identifier precedence: shipmentId > waybill > orderId. Extras are ignored, never a 400. */
export interface ListShipmentCommentsParams {
  orderId?: string;
  shipmentId?: string;
  waybill?: string;
}

export interface ListShipmentCommentsResult {
  comments: ShipmentComment[];
  count: number;
  truncated: boolean;
}

export async function addShipmentComment(
  payload: AddShipmentCommentPayload
): Promise<ShipmentComment> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-addShipmentCommentV4"), payload);
  return (response.data?.data ?? response.data ?? {}) as ShipmentComment;
}

export async function editComment(payload: EditCommentPayload): Promise<ShipmentComment> {
  const response = await apiClient.post(urlBuilder.buildUrl("order-editCommentV4"), payload);
  return (response.data?.data ?? response.data ?? {}) as ShipmentComment;
}

export async function listShipmentComments(
  params: ListShipmentCommentsParams
): Promise<ListShipmentCommentsResult> {
  const response = await apiClient.post(
    urlBuilder.buildUrl("order-listShipmentCommentsV4"),
    params
  );
  const data = (response.data?.data ?? response.data ?? {}) as Partial<ListShipmentCommentsResult>;
  return {
    comments: data.comments ?? [],
    count: data.count ?? data.comments?.length ?? 0,
    truncated: data.truncated ?? false,
  };
}
